import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ShieldCheck,
  Heart,
  Truck,
  CheckCircle2,
  Sparkles,
  Box,
  Droplets,
  Feather,
  Stethoscope,
  Wheat,
  CreditCard,
  Smartphone,
  Building2,
  Lock,
  ArrowRight,
  QrCode,
  Landmark,
  Zap,
  Copy,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

import { SiteShell } from "@/components/site-shell";
import { useCampaigns, useCreateDonation, useOrganisations, usePrograms } from "@/lib/queries";
import {
  causes,
  donationAmounts,
  formatINR,
  getCause,
  getInstitution,
  inKindCatalog,
  type InKindItem,
} from "@/data/sample";
import { campaigns, getPartner, programs } from "@/data/network";

type DonateSearch = {
  cause?: string;
  institution?: string;
  campaign?: string;
  org?: string;
  program?: string;
  freq?: string;
  amount?: number;
};

export const Route = createFileRoute("/donate")({
  validateSearch: (s: Record<string, unknown>): DonateSearch => ({
    cause: typeof s.cause === "string" ? s.cause : undefined,
    institution: typeof s.institution === "string" ? s.institution : undefined,
    campaign: typeof s.campaign === "string" ? s.campaign : undefined,
    org: typeof s.org === "string" ? s.org : undefined,
    program: typeof s.program === "string" ? s.program : undefined,
    freq: s.freq === "monthly" ? "monthly" : s.freq === "once" ? "once" : undefined,
    amount: s.amount != null && !Number.isNaN(Number(s.amount)) ? Number(s.amount) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Donate Cash or In-Kind Material — Manavta" },
      {
        name: "description",
        content:
          "Donate cash with instant 80G tax exemption or sponsor green fodder, dry straw, bird grains and medical kits for verified panjrapoles.",
      },
    ],
  }),
  component: Donate,
});

// Foundation's receiving bank account — shown on the "Bank Transfer" gateway step.
const bankDetails = {
  accountName: "Manavta",
  accountNumber: "501002345678901",
  ifsc: "ICIC0001234",
  bankName: "ICICI Bank",
  branch: "CG Road, Ahmedabad",
};

const upiId = "manavta@icici";

function isFinderDark(r: number, c: number) {
  if (r === 0 || r === 6 || c === 0 || c === 6) return true;
  if (r >= 2 && r <= 4 && c >= 2 && c <= 4) return true;
  return false;
}

// A deterministic, decorative QR-code-style pattern for the "QR Code Payment" step
// (this is a simulated gateway — no real backend QR is generated).
function PseudoQrCode({ size = 176 }: { size?: number }) {
  const n = 21;
  const cells: boolean[] = [];
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      const inTL = r < 7 && c < 7;
      const inTR = r < 7 && c >= n - 7;
      const inBL = r >= n - 7 && c < 7;
      if (inTL) cells.push(isFinderDark(r, c));
      else if (inTR) cells.push(isFinderDark(r, c - (n - 7)));
      else if (inBL) cells.push(isFinderDark(r - (n - 7), c));
      else cells.push((r * 5 + c * 3 + r * c) % 7 < 3);
    }
  }
  return (
    <div
      role="img"
      aria-label="QR code for UPI payment"
      className="grid shrink-0 rounded-xl bg-white p-2.5 shadow-inner"
      style={{
        width: size,
        height: size,
        gridTemplateColumns: `repeat(${n}, 1fr)`,
        gridTemplateRows: `repeat(${n}, 1fr)`,
      }}
    >
      {cells.map((dark, i) => (
        <div key={i} className={dark ? "bg-[#1a1a2e]" : "bg-transparent"} />
      ))}
    </div>
  );
}

function copyText(text: string, label: string) {
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.error("Couldn't copy — please copy manually"));
}

function Donate() {
  const sp = Route.useSearch();
  const navigate = useNavigate();
  const { data: dbOrgs = [] } = useOrganisations({ all: true });
  const { data: dbCampaigns = [] } = useCampaigns({ all: true });
  const { data: dbPrograms = [] } = usePrograms();
  const createDonation = useCreateDonation();

  const campaign = sp.campaign ? campaigns.find((c) => c.id === sp.campaign) : undefined;
  const program = sp.program ? programs.find((p) => p.id === sp.program) : undefined;
  const cause = sp.cause
    ? getCause(sp.cause)
    : campaign?.causeId
      ? getCause(campaign.causeId)
      : undefined;
  const org = getPartner(sp.org ?? campaign?.orgId ?? program?.orgId ?? "");
  const inst = sp.institution
    ? getInstitution(sp.institution)
    : org?.institutionId
      ? getInstitution(org.institutionId)
      : cause
        ? getInstitution(cause.institutionId)
        : undefined;

  // Donation mode: Monetary vs In-Kind Material Seva
  const [sevaMode, setSevaMode] = useState<"monetary" | "inkind">("monetary");

  // Monetary state
  const [amount, setAmount] = useState<number>(sp.amount ?? program?.monthly ?? 1001);
  const [type, setType] = useState<"once" | "monthly">(
    sp.freq === "monthly" || program ? "monthly" : "once",
  );

  // In-Kind User-Described State
  const [customItemName, setCustomItemName] = useState("Green Fodder (Fresh Chara)");
  const [itemCategory, setItemCategory] = useState("Fodder & Feed");
  const [customQuantity, setCustomQuantity] = useState<number>(100);
  const [customUnit, setCustomUnit] = useState("Kg");
  const [estimatedValue, setEstimatedValue] = useState<number>(2500);
  const [deliveryType, setDeliveryType] = useState<"vendor_dispatch" | "self_delivery">(
    "vendor_dispatch",
  );
  const [materialNotes, setMaterialNotes] = useState("");

  // Common Donor state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pan, setPan] = useState("");
  const [anon, setAnon] = useState(false);
  const [message, setMessage] = useState("");

  // Payment Gateway Modal state
  const [showGateway, setShowGateway] = useState(false);
  const [gatewayStep, setGatewayStep] = useState<"choose" | "qr" | "bank" | "razorpay">("choose");
  const [paymentMethod, setPaymentMethod] = useState<"upi" | "card" | "netbanking">("upi");
  const [processing, setProcessing] = useState(false);

  const finalDonationAmount =
    sevaMode === "monetary" ? amount : estimatedValue > 0 ? estimatedValue : 1000;

  const summaryTarget =
    campaign?.title ??
    program?.title ??
    cause?.title ??
    (inst ? `Direct General Seva Fund — ${inst.name}` : undefined) ??
    org?.name ??
    "Manavta — General Panjrapole & Gaushala Seva Fund";

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setGatewayStep("choose");
    setShowGateway(true);
  }

  async function executePayment() {
    setProcessing(true);
    const orgSlug = org?.id ?? inst?.id;
    const dbOrg = dbOrgs.find((o) => o.slug === orgSlug);
    const dbCampaign = dbCampaigns.find((c) => c.slug === (campaign?.id ?? cause?.id));
    const dbProgram = (dbPrograms as any[]).find((p) => p.slug === program?.id);

    try {
      const res = await createDonation.mutateAsync({
        donor_name: name,
        donor_email: email,
        donor_phone: phone,
        donor_pan: pan || undefined,
        anonymous: anon,
        amount: finalDonationAmount,
        frequency: type,
        donation_type: sevaMode,
        item_name: sevaMode === "inkind" ? `${customItemName} (${itemCategory})` : undefined,
        quantity: sevaMode === "inkind" ? `${customQuantity} ${customUnit}` : undefined,
        org_id: dbOrg?.id ?? dbCampaign?.org_id ?? dbProgram?.org_id ?? inst?.id ?? null,
        campaign_id: dbCampaign?.id ?? null,
        program_id: dbProgram?.id ?? null,
        target_label: summaryTarget,
        message: materialNotes || message || undefined,
      });

      setProcessing(false);
      setShowGateway(false);
      toast.success("Payment successful! Generating 80G tax receipt…");
      navigate({
        to: "/donate/success",
        search: {
          id: res.id,
          amount: res.amount,
          target: res.target_label ?? undefined,
          freq: res.frequency,
          receipt: res.receipt_no ?? undefined,
        },
      });
    } catch (err) {
      setProcessing(false);
      toast.error(
        err instanceof Error ? err.message : "Payment processing failed. Please try again.",
      );
    }
  }

  return (
    <SiteShell>
      {/* Main Donation Form Section */}
      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <form onSubmit={handleFormSubmit} className="grid gap-10 lg:grid-cols-12">
          {/* Left Column: Seva Details Selector */}
          <div className="lg:col-span-7 space-y-8">
            {sevaMode === "monetary" ? (
              /* MONETARY SEVA FORM */
              <div className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-6">
                <div className="flex items-center justify-between border-b border-border/80 pb-4">
                  <h2 className="font-display text-lg font-bold text-[var(--maroon-deep)]">
                    Donation Amount
                  </h2>
                  <div className="flex rounded-full bg-muted/60 p-1 text-xs">
                    <button
                      type="button"
                      onClick={() => setType("once")}
                      className={`rounded-full px-3 py-1 font-semibold transition ${type === "once" ? "bg-white shadow text-foreground" : "text-muted-foreground"}`}
                    >
                      One-time
                    </button>
                    <button
                      type="button"
                      onClick={() => setType("monthly")}
                      className={`rounded-full px-3 py-1 font-semibold transition ${type === "monthly" ? "bg-[var(--saffron)] text-[var(--maroon-deep)] font-bold shadow" : "text-muted-foreground"}`}
                    >
                      Monthly Seva
                    </button>
                  </div>
                </div>

                {/* Preset Chips */}
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 block">
                    Select Preset Amount
                  </label>
                  <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4">
                    {donationAmounts.map((amt) => (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => setAmount(amt)}
                        className={`rounded-xl border p-3 text-center transition-all ${
                          amount === amt
                            ? "border-[var(--maroon)] bg-[var(--cream-2)] text-[var(--maroon-deep)] font-bold ring-2 ring-[var(--maroon)]"
                            : "border-border/80 bg-background hover:bg-muted/40 font-medium text-foreground/80"
                        }`}
                      >
                        <span className="text-xs text-muted-foreground block">₹</span>
                        <span className="text-base font-extrabold">
                          {amt.toLocaleString("en-IN")}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Amount Input */}
                <div>
                  <label className="text-xs font-medium text-foreground/80 mb-1.5 block">
                    Or Enter Custom Amount
                  </label>
                  <div className="flex items-center rounded-xl border border-border bg-background px-3 py-2.5">
                    <span className="text-sm font-bold text-muted-foreground pr-2">₹</span>
                    <input
                      type="number"
                      min={100}
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value))}
                      className="w-full bg-transparent text-lg font-bold outline-none"
                    />
                  </div>
                </div>
              </div>
            ) : (
              /* IN-KIND MATERIAL SEVA FORM */
              <div className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-6">
                <div className="border-b border-border/80 pb-4 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="font-display text-lg font-bold text-[var(--maroon-deep)]">
                      Specify Your In-Kind Donation
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Describe what material, supplies or feed you wish to donate or sponsor
                    </p>
                  </div>
                  <span className="rounded-full bg-[var(--maroon)]/10 px-3 py-1 text-xs font-bold text-[var(--maroon)] shrink-0">
                    Material Seva
                  </span>
                </div>

                {/* Quick Idea Fill Chips */}
                <div className="rounded-xl border border-border/60 bg-muted/20 p-3.5 space-y-2">
                  <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">
                    💡 Quick Fill Ideas (Click to auto-populate form)
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      {
                        name: "Green Fodder (Fresh Chara)",
                        cat: "Fodder & Feed",
                        qty: 100,
                        unit: "Kg",
                        val: 3000,
                      },
                      {
                        name: "Dry Straw Bales (Sookha Chara)",
                        cat: "Fodder & Feed",
                        qty: 25,
                        unit: "Bales",
                        val: 5000,
                      },
                      {
                        name: "Avian Grain & Seed Mix",
                        cat: "Bird Care & Grains",
                        qty: 50,
                        unit: "Kg",
                        val: 2000,
                      },
                      {
                        name: "Veterinary Emergency First Aid Kit",
                        cat: "Veterinary Medicines",
                        qty: 5,
                        unit: "Kits",
                        val: 4500,
                      },
                      {
                        name: "Tarpaulin Cover for Cattle Shed",
                        cat: "Shelter & Bedding",
                        qty: 2,
                        unit: "Sheets",
                        val: 3500,
                      },
                    ].map((idea) => (
                      <button
                        key={idea.name}
                        type="button"
                        onClick={() => {
                          setCustomItemName(idea.name);
                          setItemCategory(idea.cat);
                          setCustomQuantity(idea.qty);
                          setCustomUnit(idea.unit);
                          setEstimatedValue(idea.val);
                        }}
                        className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground hover:bg-[var(--cream-2)] hover:border-[var(--maroon)]/40 transition"
                      >
                        + {idea.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 1. Item Name & Category */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-bold text-foreground mb-1 block">
                      Material / Item Description *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Green Fodder, Medical Gauze, Bird Grains..."
                      value={customItemName}
                      onChange={(e) => setCustomItemName(e.target.value)}
                      className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-xs font-medium outline-none focus:ring-2 focus:ring-[var(--maroon)]"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-foreground mb-1 block">
                      Material Category
                    </label>
                    <select
                      value={itemCategory}
                      onChange={(e) => setItemCategory(e.target.value)}
                      className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-xs font-medium outline-none focus:ring-2 focus:ring-[var(--maroon)]"
                    >
                      <option value="Fodder & Feed">🌾 Fodder & Cattle Feed</option>
                      <option value="Veterinary Medicines">
                        🏥 Veterinary Medicines & Supplies
                      </option>
                      <option value="Shelter & Bedding">🛏️ Shelter & Bedding Tarpaulins</option>
                      <option value="Bird Care & Grains">🐦 Bird Care & Grains</option>
                      <option value="Water Equipment">🚰 Water Equipment & Troughs</option>
                      <option value="Other Material">📦 Other In-Kind Supplies</option>
                    </select>
                  </div>
                </div>

                {/* 2. Quantity, Unit & Estimated Value */}
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <label className="text-xs font-bold text-foreground mb-1 block">
                      Quantity *
                    </label>
                    <input
                      type="number"
                      min={1}
                      required
                      value={customQuantity}
                      onChange={(e) => setCustomQuantity(Number(e.target.value))}
                      className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-xs font-bold outline-none focus:ring-2 focus:ring-[var(--maroon)]"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-foreground mb-1 block">Unit</label>
                    <select
                      value={customUnit}
                      onChange={(e) => setCustomUnit(e.target.value)}
                      className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-xs font-medium outline-none focus:ring-2 focus:ring-[var(--maroon)]"
                    >
                      <option value="Kg">Kg</option>
                      <option value="Bags">Bags</option>
                      <option value="Bales">Bales</option>
                      <option value="Truckloads">Truckloads</option>
                      <option value="Kits">Kits</option>
                      <option value="Boxes">Boxes</option>
                      <option value="Sheets">Sheets</option>
                      <option value="Units">Units</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-foreground mb-1 block">
                      Estimated Value (₹) *
                    </label>
                    <div className="flex items-center rounded-xl border border-border bg-background px-3 py-2.5 focus-within:ring-2 focus-within:ring-[var(--maroon)]">
                      <span className="text-xs font-bold text-muted-foreground pr-1.5">₹</span>
                      <input
                        type="number"
                        min={100}
                        required
                        value={estimatedValue}
                        onChange={(e) => setEstimatedValue(Number(e.target.value))}
                        className="w-full bg-transparent text-xs font-bold outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* 3. Delivery Method */}
                <div>
                  <label className="text-xs font-bold text-foreground mb-2 block">
                    Fulfillment / Dispatch Method
                  </label>
                  <div className="grid grid-cols-2 gap-2.5">
                    <button
                      type="button"
                      onClick={() => setDeliveryType("vendor_dispatch")}
                      className={`rounded-xl border p-3 text-left transition ${
                        deliveryType === "vendor_dispatch"
                          ? "border-[var(--maroon)] bg-[var(--cream-2)] ring-1 ring-[var(--maroon)]"
                          : "border-border bg-background hover:bg-muted/30"
                      }`}
                    >
                      <Truck className="h-4 w-4 text-[var(--maroon)] mb-1" />
                      <p className="text-xs font-bold">Local Vendor Dispatch</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Manavta procures directly from local farmers & delivers
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeliveryType("self_delivery")}
                      className={`rounded-xl border p-3 text-left transition ${
                        deliveryType === "self_delivery"
                          ? "border-[var(--maroon)] bg-[var(--cream-2)] ring-1 ring-[var(--maroon)]"
                          : "border-border bg-background hover:bg-muted/30"
                      }`}
                    >
                      <Box className="h-4 w-4 text-[var(--saffron-deep)] mb-1" />
                      <p className="text-xs font-bold">Self Drop-off / Shipping</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        You drop off goods directly at shelter gates or ship courier
                      </p>
                    </button>
                  </div>
                </div>

                {/* 4. Notes / Instructions */}
                <div>
                  <label className="text-xs font-bold text-foreground mb-1 block">
                    Instructions / Message for Shelter (Optional)
                  </label>
                  <textarea
                    rows={2}
                    placeholder="e.g. Delivery planned for Saturday morning, or contact vendor..."
                    value={materialNotes}
                    onChange={(e) => setMaterialNotes(e.target.value)}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-[var(--maroon)]"
                  />
                </div>
              </div>
            )}

            {/* Donor Personal & Tax Info Form */}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-4">
              <h2 className="font-display text-base font-bold text-[var(--maroon-deep)] border-b border-border/80 pb-3">
                Donor Details for 80G Receipt
              </h2>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-foreground/80 mb-1 block">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Ramesh Shah"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-medium outline-none focus:ring-2 focus:ring-[var(--maroon)]"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground/80 mb-1 block">
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 98765 43210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-medium outline-none focus:ring-2 focus:ring-[var(--maroon)]"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-foreground/80 mb-1 block">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="ramesh@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-medium outline-none focus:ring-2 focus:ring-[var(--maroon)]"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground/80 mb-1 block">
                    PAN Card (For 80G Exemption)
                  </label>
                  <input
                    type="text"
                    maxLength={10}
                    placeholder="ABCDE1234F"
                    value={pan}
                    onChange={(e) => setPan(e.target.value.toUpperCase())}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-bold outline-none tracking-wider uppercase focus:ring-2 focus:ring-[var(--maroon)]"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="anon"
                  checked={anon}
                  onChange={(e) => setAnon(e.target.checked)}
                  className="rounded border-border"
                />
                <label htmlFor="anon" className="text-xs text-foreground/75 cursor-pointer">
                  Make my donation anonymous on public donor boards
                </label>
              </div>
            </div>
          </div>

          {/* Right Column: Checkout Summary & Proceed Button */}
          <div className="lg:col-span-5 space-y-5 lg:sticky lg:top-20 lg:self-start">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-md space-y-4">
              <h3 className="font-display text-base font-bold text-[var(--maroon-deep)] border-b border-border pb-3">
                Seva Summary
              </h3>

              <div className="space-y-2.5 text-xs">
                <div className="flex justify-between text-muted-foreground">
                  <span>Target Entity</span>
                  <span className="font-bold text-foreground text-right max-w-[200px] truncate">
                    {summaryTarget}
                  </span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Seva Category</span>
                  <span className="font-bold text-foreground capitalize">{sevaMode} Seva</span>
                </div>
                {sevaMode === "inkind" && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Item Specified</span>
                    <span className="font-bold text-[var(--maroon)] text-right max-w-[200px] truncate">
                      {customQuantity} {customUnit} · {customItemName}
                    </span>
                  </div>
                )}
                <div className="border-t border-border/80 pt-3 flex justify-between items-baseline">
                  <span className="text-sm font-bold text-foreground">Total Payable</span>
                  <span className="font-display text-2xl font-extrabold text-[var(--maroon-deep)]">
                    ₹{finalDonationAmount.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              {/* Instant 80G Badge Notice */}
              <div className="rounded-xl bg-[var(--cream-2)] border border-border/80 p-3 flex items-start gap-2.5 text-xs text-foreground/80">
                <ShieldCheck className="h-4 w-4 text-[var(--maroon)] shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-[var(--maroon-deep)]">Instant 80G Tax Exemption</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Official receipt with QR verification code generated immediately after payment.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[var(--maroon-deep)] to-[var(--maroon)] py-3.5 text-sm font-bold text-white shadow-lg active:scale-98 transition"
              >
                Proceed to Payment Gateway <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </form>
      </section>

      {/* Payment Gateway Modal — 3 methods: QR Code, Bank Transfer, Razorpay */}
      {showGateway && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-border bg-background shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between bg-gradient-to-r from-[var(--maroon-deep)] to-[var(--maroon)] px-6 py-4">
              <div className="flex items-center gap-2 text-white">
                {gatewayStep !== "choose" && (
                  <button
                    type="button"
                    onClick={() => setGatewayStep("choose")}
                    className="grid h-7 w-7 place-items-center rounded-full bg-white/15 transition hover:bg-white/25"
                    aria-label="Back to payment methods"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                  </button>
                )}
                <Lock className="h-4 w-4 text-[var(--saffron)]" />
                <h3 className="font-display text-sm font-extrabold">
                  {gatewayStep === "choose" && "Choose Payment Method"}
                  {gatewayStep === "qr" && "Scan & Pay via UPI"}
                  {gatewayStep === "bank" && "Bank Transfer Details"}
                  {gatewayStep === "razorpay" && "Razorpay Secure Checkout"}
                </h3>
              </div>
              <button
                onClick={() => setShowGateway(false)}
                className="text-xs font-bold text-white/80 transition hover:text-white"
              >
                Cancel
              </button>
            </div>

            <div className="space-y-5 p-6">
              <div className="flex items-center justify-between rounded-xl border border-[var(--saffron-deep)]/20 bg-[var(--cream-2)] p-3 text-xs">
                <span className="text-muted-foreground">Paying to Manavta</span>
                <span className="font-display text-base font-extrabold text-[var(--maroon-deep)]">
                  ₹{finalDonationAmount.toLocaleString("en-IN")}
                </span>
              </div>

              {/* ── Step: choose method ─────────────────────────────────── */}
              {gatewayStep === "choose" && (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setGatewayStep("qr")}
                    className="group flex w-full items-center gap-3.5 rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[var(--saffron-deep)]/40 hover:shadow-md"
                  >
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[var(--saffron)]/15 text-[var(--saffron-deep)]">
                      <QrCode className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground">QR Code Payment</p>
                      <p className="text-xs text-muted-foreground">
                        Scan with any UPI app — instant confirmation
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-[var(--saffron-deep)]" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setGatewayStep("bank")}
                    className="group flex w-full items-center gap-3.5 rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[var(--maroon)]/40 hover:shadow-md"
                  >
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[var(--maroon)]/10 text-[var(--maroon)]">
                      <Landmark className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground">Bank Transfer</p>
                      <p className="text-xs text-muted-foreground">
                        NEFT / RTGS / IMPS directly to our account
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-[var(--maroon)]" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setGatewayStep("razorpay")}
                    className="group flex w-full items-center gap-3.5 rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#3b82f6]/50 hover:shadow-md"
                  >
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#3b82f6]/10 text-[#2563eb]">
                      <Zap className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground">Razorpay</p>
                      <p className="text-xs text-muted-foreground">
                        Cards, UPI, NetBanking & wallets
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-[#2563eb]" />
                  </button>
                </div>
              )}

              {/* ── Step: QR code ───────────────────────────────────────── */}
              {gatewayStep === "qr" && (
                <div className="space-y-4">
                  <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-muted/20 p-5">
                    <PseudoQrCode />
                    <p className="text-xs font-medium text-foreground/70">
                      Open GPay, PhonePe, Paytm or any UPI app
                    </p>
                    <button
                      type="button"
                      onClick={() => copyText(upiId, "UPI ID")}
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-1.5 font-mono text-xs font-bold text-[var(--maroon-deep)] transition hover:bg-[var(--cream-2)]"
                    >
                      {upiId} <Copy className="h-3 w-3 text-muted-foreground" />
                    </button>
                  </div>
                  <button
                    type="button"
                    disabled={processing}
                    onClick={executePayment}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--saffron-deep)] py-3 text-sm font-bold text-white shadow-md transition hover:bg-[var(--saffron-deep)]/90 disabled:opacity-60"
                  >
                    {processing ? "Confirming Payment…" : "I've Completed the Payment"}
                  </button>
                </div>
              )}

              {/* ── Step: bank transfer ─────────────────────────────────── */}
              {gatewayStep === "bank" && (
                <div className="space-y-4">
                  <div className="divide-y divide-border/70 rounded-2xl border border-border bg-muted/20">
                    {[
                      { label: "Account Name", value: bankDetails.accountName },
                      { label: "Account Number", value: bankDetails.accountNumber },
                      { label: "IFSC Code", value: bankDetails.ifsc },
                      {
                        label: "Bank & Branch",
                        value: `${bankDetails.bankName}, ${bankDetails.branch}`,
                      },
                    ].map((row) => (
                      <div
                        key={row.label}
                        className="flex items-center justify-between gap-3 px-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                            {row.label}
                          </p>
                          <p className="truncate text-sm font-bold text-foreground">{row.value}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => copyText(row.value, row.label)}
                          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground transition hover:bg-[var(--cream-2)] hover:text-[var(--maroon)]"
                          aria-label={`Copy ${row.label}`}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] leading-relaxed text-muted-foreground">
                    Please use your name as the transfer reference so we can match your 80G receipt
                    correctly.
                  </p>
                  <button
                    type="button"
                    disabled={processing}
                    onClick={executePayment}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--maroon)] py-3 text-sm font-bold text-white shadow-md transition hover:bg-[var(--maroon-deep)] disabled:opacity-60"
                  >
                    {processing ? "Confirming Transfer…" : "I've Made the Transfer"}
                  </button>
                </div>
              )}

              {/* ── Step: Razorpay ──────────────────────────────────────── */}
              {gatewayStep === "razorpay" && (
                <div className="space-y-4">
                  <div>
                    <p className="mb-2 text-xs font-bold text-foreground">Select Payment Method</p>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod("upi")}
                        className={`rounded-xl border p-2.5 text-center transition ${paymentMethod === "upi" ? "border-[#2563eb] bg-[#2563eb]/10 font-bold text-[#2563eb] ring-1 ring-[#2563eb]" : "border-border text-xs"}`}
                      >
                        <Smartphone className="mx-auto mb-1 h-4 w-4" /> UPI / GPay
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod("card")}
                        className={`rounded-xl border p-2.5 text-center transition ${paymentMethod === "card" ? "border-[#2563eb] bg-[#2563eb]/10 font-bold text-[#2563eb] ring-1 ring-[#2563eb]" : "border-border text-xs"}`}
                      >
                        <CreditCard className="mx-auto mb-1 h-4 w-4" /> Cards
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod("netbanking")}
                        className={`rounded-xl border p-2.5 text-center transition ${paymentMethod === "netbanking" ? "border-[#2563eb] bg-[#2563eb]/10 font-bold text-[#2563eb] ring-1 ring-[#2563eb]" : "border-border text-xs"}`}
                      >
                        <Building2 className="mx-auto mb-1 h-4 w-4" /> NetBanking
                      </button>
                    </div>
                  </div>

                  {paymentMethod === "upi" && (
                    <div className="space-y-2 rounded-xl border border-border bg-muted/20 p-3 text-center text-xs">
                      <p className="font-medium text-foreground/80">Scan & Pay via any UPI App</p>
                      <div className="inline-block rounded-lg border border-border bg-white p-2 font-mono text-xs font-bold text-[var(--maroon-deep)]">
                        {upiId}
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    disabled={processing}
                    onClick={executePayment}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2563eb] py-3 text-sm font-bold text-white shadow-md transition hover:bg-[#1d4ed8] disabled:opacity-60"
                  >
                    {processing
                      ? "Processing Payment…"
                      : `Pay ₹${finalDonationAmount.toLocaleString("en-IN")} Now`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </SiteShell>
  );
}
